/*
	Component for managing default matter permissions at the tenant/firm level.
	These permissions are applied to newly created matters by default.
*/

import React, { Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import MatterPermissionsEditor from "./MatterPermissionsEditor";
import TwoColumnWrapper from "./TwoColumnWrapper";
import { IconSettings } from "@tabler/icons-react";
import { LabeledSwitch } from "./AsyncSwitch";
import {
	getDefaultMatterPermissions,
	updateDefaultMatterPermissions,
} from "../actions/permissionsActions";
import { Loader } from "@mantine/core";

const initialPermissions = {
	matterPermissionGlobalSetting: "allUsers",
	userAllowList: [],
	groupAllowList: [],
};

class DefaultMatterPermissions extends Component {
	constructor(props) {
		super(props);
		this.state = {
			loading: true,
			permissions: initialPermissions,
			saving: false,
			allowSelfAddToMatter: false,
		};
		this.handlePermissionsChange = this.handlePermissionsChange.bind(this);
		this.handleAllowSelfAddChange =
			this.handleAllowSelfAddChange.bind(this);
		this.submit = this.submit.bind(this);
	}

	componentDidMount() {
		this.props.getDefaultMatterPermissions();
	}

	static getDerivedStateFromProps(props, state) {
		if (state.loading && !props.loading) {
			return {
				loading: false,
				permissions: props.defaultMatterPermissions,
				allowSelfAddToMatter:
					props.defaultMatterPermissions.allowSelfAddToMatter ||
					false,
			};
		}
		return null;
	}

	handlePermissionsChange(permissions) {
		// if the global setting is "allUsers", we want to default the allowSelfAddToMatter setting to false and hide the option since it wouldn't have any effect
		const updates = { permissions };
		if (permissions.matterPermissionGlobalSetting === "allUsers") {
			updates.allowSelfAddToMatter = false;
		}
		this.setState(updates);
	}

	handleAllowSelfAddChange() {
		this.setState({
			allowSelfAddToMatter: !this.state.allowSelfAddToMatter,
		});
	}

	submit() {
		this.props.updateDefaultMatterPermissions({
			...this.state.permissions,
			allowSelfAddToMatter: this.state.allowSelfAddToMatter,
		});
	}

	render() {
		if (this.state.loading || this.props.loading) {
			return (
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						padding: 40,
					}}
				>
					<Loader />
				</div>
			);
		}

		return (
			<TwoColumnWrapper
				headingText="Default Matter Permissions"
				iconComponent={
					<IconSettings
						size={16}
						style={{
							verticalAlign: "text-top",
						}}
					/>
				}
				canSubmit={!this.state.saving}
				submit={this.submit}
				submitButtonText={
					this.state.saving ? "Saving..." : "Save Default Permissions"
				}
				leftChildren={
					<div>
						<MatterPermissionsEditor
							labelType="DEFAULT_MATTER_PERMISSIONS"
							initialPermissions={this.state.permissions}
							availableUsers={this.props.availableUsers}
							availableGroups={this.props.permissionsGroups}
							onChange={this.handlePermissionsChange}
						/>
						{this.state.permissions
							.matterPermissionGlobalSetting !== "allUsers" && (
							<div style={{ marginTop: 20 }}>
								<LabeledSwitch
									upperLabel="ADD SELF WHEN CREATING MATTERS"
									label="Allow users to add themselves to new matters they create (even if they would otherwise not have access to a new Matter)"
									switchValue={
										this.state.allowSelfAddToMatter
									}
									handleSwitchValueChange={
										this.handleAllowSelfAddChange
									}
								/>
							</div>
						)}
						<div style={{ marginTop: 20, color: "#666" }}>
							<p>
								These settings will be applied automatically to
								all newly created Matters. Users can still
								modify Permissions for individual Matters after
								creation if they have the appropriate Rights.
							</p>
						</div>
						<div style={{ marginTop: 20 }}>
							Learn more about{" "}
							<a
								href="https://help.lexworkplace.com/docs/rights-management"
								target="_blank"
								rel="noreferrer"
							>
								Groups + Rights
							</a>{" "}
							and{" "}
							<a
								href="https://help.lexworkplace.com/docs/default-matter-permissions"
								target="_blank"
								rel="noreferrer"
							>
								Default Matter Permissions
							</a>
							.
						</div>
					</div>
				}
				rightChildren={null}
			/>
		);
	}
}

DefaultMatterPermissions.propTypes = {
	getDefaultMatterPermissions: PropTypes.func.isRequired,
	updateDefaultMatterPermissions: PropTypes.func.isRequired,
	availableUsers: PropTypes.array.isRequired,
	permissionsGroups: PropTypes.array.isRequired,
	loading: PropTypes.bool.isRequired,
	defaultMatterPermissions: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => {
	return {
		availableUsers: state.permissions.availableUsers || [],
		permissionsGroups: state.permissions.availableGroups || [],
		loading: state.permissions.defaultMatterPermissionsLoading,
		defaultMatterPermissions: state.permissions.defaultMatterPermissions,
	};
};

export default connect(mapStateToProps, {
	getDefaultMatterPermissions,
	updateDefaultMatterPermissions,
})(DefaultMatterPermissions);
